// src/features/production/components/SmartEntryModal.tsx

import { useState } from "react";
import type { UnifiedConsumptionLineInput } from "@/api/unifiedConsumption";
import { EntityPicker } from "@/shared/components/EntityPicker";
import { buildConsumptionLine } from "./consumptionLineValidation";
import { BlockedLotDowntimeModal } from "./BlockedLotDowntimeModal";

export interface DeactivatedLotInfo {
  code: string;
  lot_number: string;
  lot_id?: string;
  effective_status: string;
  reason_code?: string;
  reason_name?: string;
  responsible_department?: string;
  suggested_downtime_code?: string;
  message?: string;
}

export interface SmartEntryModalProps {
  bomItemId: string;
  scrapReasonOptions: { id: string; label: string }[];
  /**
   * AM-033 §2.1: the conversion-factor override control is gated by QC
   * permission — NOT by who is filling the rest of the form. A Production
   * user still enters consumption/scrap; only this one control is hidden
   * for non-QC users. Do NOT widen this to gate the whole modal.
   */
  currentUserHasQcPermission: boolean;
  workCenterId?: string;
  onSubmit: (line: UnifiedConsumptionLineInput) => Promise<void> | void;
  onCancel: () => void;
}

export function SmartEntryModal({
  bomItemId,
  scrapReasonOptions,
  currentUserHasQcPermission,
  workCenterId,
  onSubmit,
  onCancel,
}: SmartEntryModalProps) {
  const [lot, setLot] = useState<{ id: string; display: string } | null>(null);
  const [consumptionQuantity, setConsumptionQuantity] = useState("");
  const [scrapEnabled, setScrapEnabled] = useState(false);
  const [scrapQuantity, setScrapQuantity] = useState("");
  const [scrapReasonCode, setScrapReasonCode] = useState<string>("");
  const [adjustEnabled, setAdjustEnabled] = useState(false);
  const [adjustedNumerator, setAdjustedNumerator] = useState("");
  const [adjustedDenominator, setAdjustedDenominator] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deactivatedLot, setDeactivatedLot] = useState<DeactivatedLotInfo | null>(null);
  const [isDowntimeModalOpen, setIsDowntimeModalOpen] = useState(false);

  async function handleSubmit() {
    setError(null);
    setDeactivatedLot(null);
    const result = buildConsumptionLine({
      bomItemId,
      lot,
      consumptionQuantity,
      scrapEnabled,
      scrapQuantity,
      scrapReasonCode,
      adjustAllowed: currentUserHasQcPermission,
      adjustEnabled,
      adjustedNumerator,
      adjustedDenominator,
      adjustReason,
    });
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(result.line);
    } catch (e: any) {
      const respData = e?.response?.data || e?.data || {};
      const payload =
        respData?.code === "LOT_DEACTIVATED"
          ? respData
          : respData?.detail?.code === "LOT_DEACTIVATED"
            ? respData.detail
            : null;
      if (payload) {
        setDeactivatedLot({
          ...payload,
          lot_id: payload.lot_id || (lot ? lot.id : undefined),
          lot_number: payload.lot_number || (lot ? lot.display : ""),
        });
        setError(payload.message || "پالت انتخاب‌شده غیرفعال یا معلق است.");
      } else {
        setError(e instanceof Error ? e.message : "خطا در ثبت.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(4px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        direction: "rtl",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <div
        className="brand-card-extand am033-smart-entry-modal"
        role="dialog"
        aria-modal="true"
        style={{
          padding: "1.5rem",
          width: "100%",
          maxWidth: "540px",
          boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "0.75rem",
            marginBottom: "1.25rem",
            borderBottom: "1px solid var(--color-border, #E4DCC8)",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "1.1rem",
              fontWeight: 800,
              color: "#007270",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span>📦</span>
            <span>ثبت مصرف پالت (قلم BOM: {bomItemId})</span>
          </h3>
          <button
            type="button"
            onClick={onCancel}
            style={{ background: "none", border: "none", color: "#888", fontSize: "1.2rem", cursor: "pointer" }}
          >
            ✕
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          <div>
            <label
              style={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: "#2B2B2B",
                display: "block",
                marginBottom: "6px",
              }}
            >
              شناسه پالت (Inventory Lot)
            </label>
            <EntityPicker
              model="inventory.inventorylot"
              value={lot}
              onChange={setLot}
              placeholder="انتخاب یا جستجوی شناسه پالت..."
            />
          </div>

          <div>
            <label
              style={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: "#2B2B2B",
                display: "block",
                marginBottom: "6px",
              }}
            >
              مقدار مصرف
            </label>
            <input
              type="number"
              value={consumptionQuantity}
              onChange={(e) => setConsumptionQuantity(e.target.value)}
              data-testid="consumption-quantity-input"
              placeholder="مقدار مصرفی پالت..."
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "10px",
                border: "1px solid var(--color-border, #E4DCC8)",
                background: "#FFFFFF",
                color: "#2B2B2B",
                fontSize: "0.9rem",
                outline: "none",
              }}
            />
          </div>

          <div
            style={{
              padding: "10px 14px",
              borderRadius: "10px",
              background: "rgba(0,0,0,0.02)",
              border: "1px solid var(--color-border, #E4DCC8)",
            }}
          >
            <label
              className="am033-toggle"
              style={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: "#2B2B2B",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={scrapEnabled}
                onChange={(e) => setScrapEnabled(e.target.checked)}
                data-testid="scrap-toggle"
                style={{ width: "16px", height: "16px", accentColor: "#007270" }}
              />
              <span>ثبت ضایعات همین پالت</span>
            </label>
            {scrapEnabled && (
              <div
                className="am033-smart-entry-modal__scrap-fields"
                style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginTop: "10px" }}
              >
                <div>
                  <label
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      color: "#555",
                      display: "block",
                      marginBottom: "4px",
                    }}
                  >
                    مقدار ضایعات
                  </label>
                  <input
                    type="number"
                    value={scrapQuantity}
                    onChange={(e) => setScrapQuantity(e.target.value)}
                    data-testid="scrap-quantity-input"
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: "8px",
                      border: "1px solid var(--color-border, #E4DCC8)",
                      background: "#FFFFFF",
                      fontSize: "0.85rem",
                      outline: "none",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      color: "#555",
                      display: "block",
                      marginBottom: "4px",
                    }}
                  >
                    علت ضایعات
                  </label>
                  <select
                    value={scrapReasonCode}
                    onChange={(e) => setScrapReasonCode(e.target.value)}
                    data-testid="scrap-reason-select"
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: "8px",
                      border: "1px solid var(--color-border, #E4DCC8)",
                      background: "#FFFFFF",
                      fontSize: "0.85rem",
                      outline: "none",
                    }}
                  >
                    <option value="">— انتخاب کنید —</option>
                    {scrapReasonOptions.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* AM-033 §2.1: rendered ONLY for QC-permitted users. */}
          {currentUserHasQcPermission && (
            <div
              style={{
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(2, 132, 199, 0.04)",
                border: "1px solid rgba(2, 132, 199, 0.2)",
              }}
            >
              <label
                className="am033-toggle"
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color: "#0369A1",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={adjustEnabled}
                  onChange={(e) => setAdjustEnabled(e.target.checked)}
                  data-testid="adjust-conversion-toggle"
                  style={{ width: "16px", height: "16px", accentColor: "#0284C7" }}
                />
                <span>تغییر ضریب تبدیل این پالت (کنترل کیفیت / QC)</span>
              </label>
              {adjustEnabled && (
                <div
                  className="am033-smart-entry-modal__adjust-fields"
                  style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "10px" }}
                >
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                    <div>
                      <label
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: "#555",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        صورت (numerator)
                      </label>
                      <input
                        type="number"
                        value={adjustedNumerator}
                        onChange={(e) => setAdjustedNumerator(e.target.value)}
                        data-testid="adjusted-numerator-input"
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          borderRadius: "8px",
                          border: "1px solid var(--color-border, #E4DCC8)",
                          background: "#FFFFFF",
                          fontSize: "0.85rem",
                          outline: "none",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: "#555",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        مخرج (denominator)
                      </label>
                      <input
                        type="number"
                        value={adjustedDenominator}
                        onChange={(e) => setAdjustedDenominator(e.target.value)}
                        data-testid="adjusted-denominator-input"
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          borderRadius: "8px",
                          border: "1px solid var(--color-border, #E4DCC8)",
                          background: "#FFFFFF",
                          fontSize: "0.85rem",
                          outline: "none",
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        color: "#555",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      دلیل تغییر
                    </label>
                    <textarea
                      value={adjustReason}
                      onChange={(e) => setAdjustReason(e.target.value)}
                      data-testid="adjust-reason-input"
                      style={{
                        width: "100%",
                        minHeight: "60px",
                        padding: "8px 10px",
                        borderRadius: "8px",
                        border: "1px solid var(--color-border, #E4DCC8)",
                        background: "#FFFFFF",
                        fontSize: "0.85rem",
                        outline: "none",
                        resize: "vertical",
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {deactivatedLot && (
            <div
              className="am056-deactivated-alert"
              data-testid="deactivated-lot-alert"
              style={{
                background: "#FEF2F2",
                border: "1px solid #F87171",
                borderRadius: "10px",
                padding: "14px",
                color: "#991B1B",
              }}
            >
              <div style={{ fontWeight: 800, marginBottom: "8px" }}>
                ⚠️ پالت غیرقابل مصرف در کارگاه (معلق / توقف اداری)
              </div>
              <div style={{ fontSize: "0.875rem", lineHeight: "1.7" }}>
                <div>
                  <strong>شماره پالت:</strong> {deactivatedLot.lot_number}
                </div>
                <div>
                  <strong>وضعیت مؤثر:</strong> {deactivatedLot.effective_status}
                </div>
                {deactivatedLot.responsible_department && (
                  <div>
                    <strong>واحد مسئول پیگیری:</strong> {deactivatedLot.responsible_department}
                  </div>
                )}
                {deactivatedLot.reason_name && (
                  <div>
                    <strong>علت تعلیق:</strong> {deactivatedLot.reason_name}
                  </div>
                )}
                {deactivatedLot.suggested_downtime_code && (
                  <div>
                    <strong>کد پیشنهادی توقف خط:</strong> <code>{deactivatedLot.suggested_downtime_code}</code>
                  </div>
                )}
              </div>
              <button
                type="button"
                data-testid="open-downtime-modal-button"
                onClick={() => setIsDowntimeModalOpen(true)}
                style={{
                  marginTop: "12px",
                  padding: "8px 16px",
                  background: "#DC2626",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                🛑 ثبت توقف ایستگاه کاری (Downtime)
              </button>
            </div>
          )}

          {error && !deactivatedLot && (
            <div
              className="am033-smart-entry-modal__error"
              data-testid="form-error"
              style={{
                padding: "0.85rem 1.1rem",
                borderRadius: "10px",
                background: "rgba(239, 68, 68, 0.12)",
                border: "1px solid #EF4444",
                color: "#B91C1C",
                fontSize: "0.85rem",
                fontWeight: 600,
              }}
            >
              🚨 {error}
            </div>
          )}

          <div
            className="am033-smart-entry-modal__actions"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
              marginTop: "0.5rem",
              paddingTop: "1rem",
              borderTop: "1px solid var(--color-border, #E4DCC8)",
            }}
          >
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              data-testid="cancel-button"
              style={{
                padding: "8px 16px",
                borderRadius: "10px",
                background: "none",
                border: "1px solid var(--color-border, #E4DCC8)",
                color: "#555",
                fontSize: "0.85rem",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="submit-button"
              style={{
                padding: "8px 24px",
                borderRadius: "10px",
                background: "#007270",
                color: "#FFF",
                border: "none",
                fontSize: "0.9rem",
                fontWeight: 800,
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? "در حال ثبت…" : "ثبت"}
            </button>
          </div>
        </div>

        {deactivatedLot && (
          <BlockedLotDowntimeModal
            isOpen={isDowntimeModalOpen}
            onClose={() => setIsDowntimeModalOpen(false)}
            lotId={deactivatedLot.lot_id}
            lotNumber={deactivatedLot.lot_number}
            responsibleDepartment={deactivatedLot.responsible_department}
            reasonName={deactivatedLot.reason_name}
            suggestedDowntimeCode={deactivatedLot.suggested_downtime_code}
            defaultWorkCenterId={workCenterId}
            onSuccess={() => {
              setIsDowntimeModalOpen(false);
              onCancel();
            }}
          />
        )}
      </div>
    </div>
  );
}
